import client from './client';

/**
 * Get item codes with optional search
 * @param {string} searchTerm - Optional search term
 * @param {number} limit - Maximum number of results
 * @returns {Promise<Object>} Item codes list
 */
export async function getItemCodes(searchTerm = '', limit = 100) {
  try {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    if (limit) params.limit = limit;

    const response = await client.get('/api/items', { params });
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching item codes:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch item codes'
    };
  }
}

/**
 * Get item details by item code
 * @param {string} itemCode - The item code
 * @returns {Promise<Object>} Item details
 */
export async function getItemDetails(itemCode) {
  try {
    const response = await client.get(`/api/items/${itemCode}`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching item details:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch item details'
    };
  }
}

/**
 * Get all brands
 * @returns {Promise<Object>} List of brands
 */
export async function getBrands() {
  try {
    const response = await client.get('/api/items/brands');
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching brands:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch brands'
    };
  }
}

/**
 * Get all categories
 * @returns {Promise<Object>} List of categories
 */
export async function getCategories() {
  try {
    const response = await client.get('/api/items/categories');
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching categories:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch categories'
    };
  }
}

/**
 * Get all seasons
 * @returns {Promise<Object>} List of seasons
 */
export async function getSeasons() {
  try {
    const response = await client.get('/api/items/seasons');
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching seasons:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch seasons'
    };
  }
}

export async function getFrameShapes() {
  try {
    const response = await client.get('/api/items/frame-shapes');
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching frame shapes:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch frame shapes'
    };
  }
}

export async function getItemModels() {
  try {
    const response = await client.get('/api/items/item-models');
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching item models:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch item models'
    };
  }
}

/**
 * Get items list with pagination, filtering, and sorting
 * @param {number} limit - Number of records per page
 * @param {number} offset - Number of records to skip
 * @param {string} searchTerm - Optional search term
 * @param {Array<string>} brands - Optional array of brand filters
 * @param {Array<string>} categories - Optional array of category filters
 * @param {Array<string>} seasons - Optional array of season filters
 * @param {string} sortBy - Column to sort by
 * @param {string} sortOrder - Sort order (ASC or DESC)
 * @returns {Promise<Object>} Items list with pagination
 */
export async function getItemsList(limit = 50, offset = 0, searchTerm = '', brands = [], categories = [], seasons = [], sortBy = 'item_code', sortOrder = 'ASC', hasImage = null, inStock = null, hasPo = null, frameShapes = [], itemModels = [], aiFrameShapes = [], noAiFrameShape = false, frameShapeMismatch = false, frameShapeMatch = false, noPhotoGrOverdue = false, photoNoOrMismatch = false) {
  try {
    const params = { limit, offset };
    if (searchTerm) params.search = searchTerm;
    if (brands && brands.length > 0) params.brand = brands.join(',');
    if (categories && categories.length > 0) params.category = categories.join(',');
    if (seasons && seasons.length > 0) params.season = seasons.join(',');
    if (frameShapes && frameShapes.length > 0) params.frameShape = frameShapes.join(',');
    if (aiFrameShapes && aiFrameShapes.length > 0) params.aiFrameShape = aiFrameShapes.join(',');
    if (itemModels && itemModels.length > 0) params.itemModel = itemModels.join(',');
    // Optional filters for image availability and stock
    if (hasImage !== null && hasImage !== undefined) params.hasImage = hasImage;
    if (inStock !== null && inStock !== undefined) params.inStock = inStock;
    if (hasPo !== null && hasPo !== undefined) params.hasPo = hasPo;
    if (noAiFrameShape) params.noAiFrameShape = 'true';
    if (frameShapeMismatch) params.frameShapeMismatch = 'true';
    if (frameShapeMatch) params.frameShapeMatch = 'true';
    if (noPhotoGrOverdue) params.noPhotoGrOverdue = 'true';
    if (photoNoOrMismatch) params.photoNoOrMismatch = 'true';
    if (sortBy) params.sortBy = sortBy;
    if (sortOrder) params.sortOrder = sortOrder;

    const response = await client.get('/api/items/list', { params });
    // Client interceptor returns response.data, so response = { success, data: { items, pagination }, message }
    const responseData = response.data || {};
    return {
      success: true,
      data: responseData.items || [],
      pagination: responseData.pagination || { total: 0, limit, offset, hasMore: false }
    };
  } catch (error) {
    console.error('Error fetching items list:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch items list'
    };
  }
}

/**
 * Export all filtered items (no pagination cap) for Excel download
 */
export async function exportItemsList(searchTerm = '', brands = [], categories = [], seasons = [], sortBy = 'item_code', sortOrder = 'ASC', hasImage = null, inStock = null, hasPo = null, frameShapes = [], itemModels = [], aiFrameShapes = [], noAiFrameShape = false, frameShapeMismatch = false, frameShapeMatch = false, noPhotoGrOverdue = false, photoNoOrMismatch = false) {
  const params = {};
  if (searchTerm) params.search = searchTerm;
  if (brands && brands.length > 0) params.brand = brands.join(',');
  if (categories && categories.length > 0) params.category = categories.join(',');
  if (seasons && seasons.length > 0) params.season = seasons.join(',');
  if (frameShapes && frameShapes.length > 0) params.frameShape = frameShapes.join(',');
  if (aiFrameShapes && aiFrameShapes.length > 0) params.aiFrameShape = aiFrameShapes.join(',');
  if (itemModels && itemModels.length > 0) params.itemModel = itemModels.join(',');
  if (hasImage !== null && hasImage !== undefined) params.hasImage = hasImage;
  if (inStock !== null && inStock !== undefined) params.inStock = inStock;
  if (hasPo !== null && hasPo !== undefined) params.hasPo = hasPo;
  if (noAiFrameShape) params.noAiFrameShape = 'true';
  if (frameShapeMismatch) params.frameShapeMismatch = 'true';
  if (frameShapeMatch) params.frameShapeMatch = 'true';
  if (noPhotoGrOverdue) params.noPhotoGrOverdue = 'true';
  if (photoNoOrMismatch) params.photoNoOrMismatch = 'true';
  if (sortBy) params.sortBy = sortBy;
  if (sortOrder) params.sortOrder = sortOrder;

  const response = await client.get('/api/items/export', { params });
  return response.data || [];
}

/**
 * Get all images for a specific item from pms_item_check
 * @param {string} itemCode - The item code
 * @returns {Promise<Object>} Item images
 */
export async function getItemImages(itemCode) {
  try {
    const response = await client.get(`/api/items/${itemCode}/images`);
    // Client interceptor returns response.data, so response = { success, data: [...images], message }
    return {
      success: true,
      data: response.data || []
    };
  } catch (error) {
    console.error('Error fetching item images:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch item images'
    };
  }
}

/**
 * Get stock and image analysis for dashboard
 * @returns {Promise<Object>} Stock and image analysis data
 */
export async function getStockImageAnalysis(filters = {}) {
  try {
    const { brands = [], categories = [], seasons = [], search = '' } = filters;
    const params = {};
    if (brands.length > 0) params.brand = brands.join(',');
    if (categories.length > 0) params.category = categories.join(',');
    if (seasons.length > 0) params.season = seasons.join(',');
    if (search) params.search = search;

    const response = await client.get('/api/items/stock-image-analysis', { params });
    return {
      success: true,
      data: response.data || {}
    };
  } catch (error) {
    console.error('Error fetching stock-image analysis:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch stock-image analysis'
    };
  }
}
